import React, { useState, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  useTheme,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography
} from '@mui/material'

import useDebounce from 'packages/ui/use-debounce'
import { SearchContainer } from './GeolocationStyles'
import ControlsTooltipWrapper from '../ControlsTooltipWrapper'

interface PlaceResult {
  displayName: { text: string; languageCode: string }
  formattedAddress: string
  location: { latitude: number; longitude: number }
}

interface SearchResponse {
  places?: PlaceResult[]
}

const useSearch = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SearchResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { i18n } = useTranslation();

  const search = useCallback(async (query: string) => {
    setLoading(true)
    try {
      const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.types',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_MAP_KEY ?? '',
        },
        body: JSON.stringify({
          textQuery: query,
          includedType: 'locality',
          languageCode: i18n.language,
        })
      })
      const results: SearchResponse = await response.json()
      setData(results)
      setError(null)
    } catch (e) {
      setData(null)
      setError(e instanceof Error ? e.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }, [i18n.language])

  return { loading, data, error, search }
}

const DEBOUNCE_DELAY = 300
const ITEM_HEIGHT = 48
const MAX_VISIBLE_ITEMS = 5

const Search = ({
  onChange = ({ latitude, longitude }: { latitude: number, longitude: number }) => { }
}) => {
  const { loading, data, search } = useSearch()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY)

  const places = data?.places ?? []
  const showResults = useMemo(() => {
    if (!data?.places) {
      return []
    }
    return isOpen && isDropdownVisible && query.trim().length >= 2 && (places.length > 0 || loading)
  }, [isOpen, data, isDropdownVisible, query, loading])

  const handleIconClick = () => {
    if (isOpen) {
      setQuery('')
      setIsDropdownVisible(false)
      setIsOpen(false)
      return
    }

    setIsOpen(true)
    // Focus le TextField après la transition CSS (300ms)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 50)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    setIsDropdownVisible(true)
    if (value.trim().length >= 2) {
      debouncedSearch(value)
    }
  }

  const handleSelectPlace = (place: PlaceResult) => {
    onChange({
      latitude: place.location.latitude,
      longitude: place.location.longitude
    })
    setQuery('')
    setIsDropdownVisible(false)
    setIsOpen(false)
  }

  return (
    <SearchContainer sx={{ flexDirection: 'row', alignItems: 'flex-start', position: 'relative' }}>
      <ControlsTooltipWrapper
        title={t('cartography.maps.controls.search')}
        arrow
        placement='right'
        disabled={isOpen}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#ffffff !important',
            borderRadius: '20px',
            height: 32,
            width: isOpen ? 340 : 32,
            transition: 'width 0.3s ease-in-out',
            boxShadow: `0 12px 16px -8px rgba(0, 0, 0, 0.24)`,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              opacity: isOpen ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              overflow: 'hidden',
            }}
          >
            <TextField
              inputRef={inputRef}
              placeholder={t('cartography.maps.controls.search')}
              size="small"
              value={query}
              onChange={handleInputChange}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '20px 0 0 20px',
                  backgroundColor: 'transparent',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '6px 12px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
          <Box
            onClick={handleIconClick}
            tabIndex={0}
            role="button"
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleIconClick()
              }
            }}
            aria-label={isOpen ? 'Fermer la recherche' : 'Ouvrir la recherche'}
            sx={{
              minWidth: 32,
              minHeight: 32,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#464646',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <i
              className={isOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-magnifying-glass'}
              style={{ fontSize: '14px' }}
            />
          </Box>
        </Box>
      </ControlsTooltipWrapper>
      {showResults && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: 340,
            marginTop: 0.5,
            maxHeight: ITEM_HEIGHT * MAX_VISIBLE_ITEMS,
            overflowY: 'auto',
            zIndex: 1000,
            borderRadius: '6px',
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding>
              {(data?.places || []).map((place, index) => (
                <ListItem
                  key={`${place.displayName.text}-${index}`}
                  onClick={() => handleSelectPlace(place)}
                  sx={{
                    cursor: 'pointer',
                    height: ITEM_HEIGHT,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    borderBottom: index < places.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  }}
                  tabIndex={0}
                  role="option"
                  aria-selected={false}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSelectPlace(place)
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap fontWeight={500}>
                        {place.displayName.text}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {place.formattedAddress}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </SearchContainer>
  )
}

export default Search